import { useState } from 'react';
import { IconExternal, Spacer } from '@zillow/constellation';
import { Button, Combobox, DropdownSelect, FormField, Image, Label, Modal, Text } from '@zillow/constellation-10';
import { Center } from '@zillow/zrs-comet';
import { US_STATES_LABEL_MAP } from '@zg-rentals/states-territories';

import TenantPlacementCSEvents from '../../model/ClickstreamEvent/TenantPlacementCSEvents';
import type { PageLocation } from '../../utils/tenantPlacementUtils';
import MapImage from '../../images/tenantPlacement/map.svg';

import { useSelector } from '../../selectors/useSelector';

import { determinePartner, isStateSupported, TENANT_PLACEMENT_PARTNERS } from '../../utils/tenantPlacementUtils';

const dropdownOptions = Object.entries(US_STATES_LABEL_MAP).map(([value, label]) => ({
  label,
  value,
}));

const TenantPlacementMultiPartnerButton = ({
  stateCode,
  pageLocation,
  partnerUrlWithParams,
  onSetSelectedState,
  selectedState,
}: {
  stateCode: string;
  pageLocation: PageLocation;
  partnerUrlWithParams: string;
  onSetSelectedState: (stateCode: string) => void;
  selectedState: string;
}) => {
  const user = useSelector((state) => state.user);

  const [ctaClickedLocation, setCtaClickedLocation] = useState<PageLocation>('top_hero');
  const [displayNoPartner, setDisplayNoPartner] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false);
    setDisplayNoPartner(false);
    onSetSelectedState('');
    setCtaClickedLocation('top_hero');
  };

  const onPrimaryCtaClickHandler = (pageLocation: PageLocation, stateCode: string) => {
    setCtaClickedLocation(pageLocation);
    if (!isStateSupported(stateCode)) {
      setIsModalOpen(true);
      return;
    }
    const partner = determinePartner(stateCode);
    trackGoToPartnerLandingPage(partner, pageLocation);
  };

  const trackGoToPartnerLandingPage = (partner: string, pageLocation: PageLocation) => {
    switch (partner) {
      case TENANT_PLACEMENT_PARTNERS.DOORSTEAD:
        TenantPlacementCSEvents.TenantPlacementGoToDoorstead({
          user,
          pageLocation,
        });
        break;
      case TENANT_PLACEMENT_PARTNERS.NOMAD:
        TenantPlacementCSEvents.TenantPlacementGoToNomad({
          user,
          pageLocation,
        });
        break;
      default:
        break;
    }
  };

  const getNextStep = (stateCode: string) => {
    const partner = determinePartner(stateCode);
    if (partner === TENANT_PLACEMENT_PARTNERS.NONE) {
      TenantPlacementCSEvents.TenantPlacementStateNotSupported({
        user,
        pageLocation: ctaClickedLocation,
      });
      setDisplayNoPartner(true);
      return;
    }
    trackGoToPartnerLandingPage(partner, ctaClickedLocation);
    closeModal();
  };

  const stateSelectorModal = () => {
    const body = !displayNoPartner ? (
      <Spacer padding="xs">
        <Text textStyle="body-sm-bold" asChild>
          <p>Which state is your property in?</p>
        </Text>
        <Text>Our dedicated partners in every state are ready to assist you.</Text>
        <Spacer paddingBottom="md" />
        <FormField>
          <Label>Select state</Label>
          <DropdownSelect
            placeholder="Select a state"
            options={dropdownOptions}
            showLabelForValue={true}
            onOptionSelect={(option: string | { value: string; label: string }) => {
              const selectedValue = typeof option === 'string' ? option : option.value;
              onSetSelectedState(selectedValue);
            }}
            renderInput={(renderProps) => {
              const { ref, ...adornmentPropsWithoutRef } = renderProps;
              return (
                <Combobox.Input {...adornmentPropsWithoutRef} data-testid={`TenantPlacementPage-Modal-SelectState`} />
              );
            }}
          />
        </FormField>
      </Spacer>
    ) : (
      <Center padding="xs">
        <Image src={MapImage} alt="map" css={{ marginBottom: '800' }} />
        <Spacer paddingBottom="md" />
        <Text textStyle="body-sm-bold" data-testid="TenantPlacementPage-Modal-NotSupported">
          We currently don&apos;t have a partner established in your state.
        </Text>
      </Center>
    );

    const useAnchor = isStateSupported(selectedState);
    const footer = !displayNoPartner ? (
      <Button
        tone="brand"
        emphasis="filled"
        onClick={() => getNextStep(selectedState)}
        disabled={selectedState === ''}
        data-testid="TenantPlacementPage-Modal-ContBtn"
        data-eventid={TenantPlacementCSEvents.TenantPlacementStateNotSupported.data_event_id}
        asChild
      >
        <a href={useAnchor ? partnerUrlWithParams : '#'} target={useAnchor ? '_blank' : ''} rel="nofollow noreferrer">
          Continue
        </a>
      </Button>
    ) : (
      <Button tone="brand" emphasis="filled" onClick={closeModal} data-testid="TenantPlacementPage-Modal-CloseBtn">
        Close
      </Button>
    );

    return (
      <Modal open={isModalOpen} size="sm" body={body} onOpenChange={(open) => !open && closeModal()} footer={footer} />
    );
  };

  const getDataEventId = (stateCode: string) => {
    if (!isStateSupported(stateCode)) {
      return TenantPlacementCSEvents.TenantPlacementStateNotSupported.data_event_id;
    }

    const partner = determinePartner(stateCode);
    if (partner === TENANT_PLACEMENT_PARTNERS.DOORSTEAD) {
      return TenantPlacementCSEvents.TenantPlacementGoToDoorstead.data_event_id;
    }

    return TenantPlacementCSEvents.TenantPlacementGoToNomad.data_event_id;
  };

  const useAnchor = isStateSupported(stateCode);
  return pageLocation === 'top_hero' ? (
    <Spacer marginTop={{ default: 'xs', lg: 'sm' }}>
      <Button
        tone="brand"
        emphasis="filled"
        onClick={() => onPrimaryCtaClickHandler('top_hero', stateCode)}
        data-testid="TenantPlacementPage-GetStartedBtn-TopHero"
        css={{ paddingBlock: '400', paddingInline: { base: '400', xxl: '600' } }}
        icon={<IconExternal size="sm" marginLeft="xs" />}
        iconPosition="end"
        asChild
        data-eventid={getDataEventId(stateCode)}
      >
        <a href={useAnchor ? partnerUrlWithParams : '#'} target={useAnchor ? '_blank' : ''} rel="nofollow noreferrer">
          Learn More
        </a>
      </Button>
      {stateSelectorModal()}
    </Spacer>
  ) : (
    <>
      <Button
        tone="neutral"
        emphasis="outlined"
        css={{ paddingBlock: '400', paddingInline: '800' }}
        onClick={() => onPrimaryCtaClickHandler('bottom_of_page', stateCode)}
        data-testid="TenantPlacementPage-GetStartedBtn-BottomOfPage"
        icon={<IconExternal size="sm" marginLeft="xs" />}
        iconPosition="end"
        asChild
        data-eventid={getDataEventId(stateCode)}
      >
        <a href={useAnchor ? partnerUrlWithParams : '#'} target={useAnchor ? '_blank' : ''} rel="nofollow noreferrer">
          Get started
        </a>
      </Button>
      {stateSelectorModal()}
    </>
  );
};

export default TenantPlacementMultiPartnerButton;
